import React from 'react'

import { Formik } from 'formik'
import { useHistory } from 'react-router-dom'

import { ZariContext } from '_zari/context'

import { Col, Form, Row, Divider, Button } from '_zari/core'
import Yup from '_zari/validation/_yup'

import { CodigosTransaccionesConstant } from 'constants/index'

import { IdGen } from 'utils'

import * as ActivoFijoService from 'services/activosFijos.service'

import * as FormGroups from './formGroups'
import * as Sections from './sections'

export const ValidationSchema = Yup.object().shape({
  codigo: Yup.string().required(),
  descripcion: Yup.string()
    .max(100)
    .required(),
  cuentaContable: Yup.string().required(),
  depreciacionAnual: Yup.number()
    .max(100)
    .notRequired(),
  fechaUso: Yup.string().required(),
  fechaAdquisicion: Yup.string().required()
})

const commonReducer = (state, action) => {
  switch (action.type) {
    case 'agregar': {
      return [
        ...state,
        {
          ...action.payload,
          _idFila: IdGen.newId()
        }
      ]
    }
    case 'eliminar':
      return state.filter(val => val._idFila !== action.payload._idFila)
    default:
      return state
  }
}

const MainForm = () => {
  const __zariContext = React.useContext(ZariContext)
  const history = useHistory()

  const [detalles, detallesDispatcher] = React.useReducer(commonReducer, [])

  const mainFormRef = React.useRef(null)

  const formInitialValues = React.useMemo(() => {
    return {
      codigo: '',
      codigoGeneradoFlag: false,
      cuentaContable: '',
      depreciacionAnual: '',
      descripcion: '',
      fechaAdquisicion: '',
      fechaEntrega: '',
      fechaUso: '',
      marca: '',
      metodoDepreciacion: '',
      modelo: '',
      numeroAutorizacion: '',
      serie: '',
      _idOficina: '',
      oficina: '',
      valorAdquisicion: ''
    }
  }, [])

  const clickSubmit = React.useCallback(() => {
    mainFormRef.current.handleSubmit()
  }, [])

  const onSubmit = React.useCallback(
    formData => {
      const submitData = {
        ...formData,
        detalles: detalles
      }

      ActivoFijoService.nuevo(submitData)
        .then(() => {
          __zariContext.snack('success', 'El registro se ha creado.')
          history.push(`/${CodigosTransaccionesConstant.ACTIVOSFIJOS_LISTA}`)
        })
        .catch(({ data: { error } }) => __zariContext.snack('error', error))
    },
    [__zariContext, history, detalles]
  )

  return (
    <>
      <Formik
        initialValues={formInitialValues}
        innerRef={mainFormRef}
        onSubmit={onSubmit}
        validationSchema={ValidationSchema}
      >
        {({ errors, touched, values, setValues, isSubmitting, handleChange, setFieldValue }) => (
          <Form>
            <Row>
              <Col md={4}>
                <FormGroups.CodigoFormGroup
                  fIsSubmitting={isSubmitting}
                  handleChange={handleChange}
                  iError={errors.codigo}
                  iTouched={touched.codigo}
                  setFieldValue={setFieldValue}
                />
                <FormGroups.DescripcionFormGroup
                  fIsSubmitting={isSubmitting}
                  iError={errors.descripcion}
                  iTouched={touched.descripcion}
                />
                <FormGroups.CuentaContableFormGroup
                  setFieldValue={setFieldValue}
                  iError={errors.cuentaContable}
                  iTouched={touched.cuentaContable}
                  iValue={values.cuentaContableInfo}
                />
                <FormGroups.DepreciacionAnualFormGroup
                  fIsSubmitting={isSubmitting}
                  iError={errors.depreciacionAnual}
                  iTouched={touched.depreciacionAnual}
                />
              </Col>
              <Col md={3}>
                <FormGroups.ModeloFormGroup
                  fIsSubmitting={isSubmitting}
                  iError={errors.modelo}
                  iTouched={touched.modelo}
                />
                <FormGroups.SerieFormGroup
                  fIsSubmitting={isSubmitting}
                  iError={errors.serie}
                  iTouched={touched.serie}
                />
                <FormGroups.MarcaFormGroup
                  fIsSubmitting={isSubmitting}
                  iError={errors.marca}
                  iTouched={touched.marca}
                />
                <FormGroups.MetodoDepreciacionFormGroup
                  fIsSubmitting={isSubmitting}
                  iError={errors.metodoDepreciacion}
                  iTouched={touched.metodoDepreciacion}
                />
              </Col>
              <Col md={3}>
                <FormGroups.NumeroAutorizacionFormGroup
                  fIsSubmitting={isSubmitting}
                  iError={errors.numeroAutorizacion}
                  iTouched={touched.numeroAutorizacion}
                />
                <FormGroups.FechaUsoFormGroup
                  fIsSubmitting={isSubmitting}
                  iError={errors.fechaUso}
                  iTouched={touched.fechaUso}
                />
                <FormGroups.FechaAdquisicionFormGroup
                  fIsSubmitting={isSubmitting}
                  iError={errors.fechaAdquisicion}
                  iTouched={touched.fechaAdquisicion}
                />
              </Col>
              <Col md={2}>
                <FormGroups.OficinaFormGroup
                  fIsSubmitting={isSubmitting}
                  iError={errors._idOficina}
                  iTouched={touched._idOficina}
                  setFieldValue={setFieldValue}
                />
                <FormGroups.FechaEntregaFormGroup
                  fIsSubmitting={isSubmitting}
                  iError={errors.fechaEntrega}
                  iTouched={touched.fechaEntrega}
                />
                <FormGroups.ValorAdquisicionFormGroup
                  fIsSubmitting={isSubmitting}
                  iError={errors.valorAdquisicion}
                  iTouched={touched.valorAdquisicion}
                />
              </Col>
            </Row>
          </Form>
        )}
      </Formik>
      <Divider noMargin />
      <Sections.DetallesCabeceraSection dispatch={detallesDispatcher} />
      <Divider noMargin />
      <Sections.DetallesTablaSection detalles={detalles} dispatch={detallesDispatcher} />
      <Divider noMargin />
      <Row>
        <Col md={12} className='text-right'>
          <Button onClick={clickSubmit}>Procesar</Button>
        </Col>
      </Row>
    </>
  )
}

export default React.memo(MainForm)
